import React, { useState, useCallback } from 'react';
import { Input } from '../UI/Input';
import { SelectInput } from '../UI/SelectInput';
import { Button } from '../UI/Button';
import { useToast } from '../../hooks/useToast';
import { ordersApi } from '../../api';
import type { Side, OrderType, TimeInForce, OrderSubmitRequest } from '../../types/api';

interface OrderFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FormData {
  symbol: string;
  side: Side;
  orderType: OrderType;
  quantity: string;
  limitPrice: string;
  timeInForce: TimeInForce;
  clientOrderId: string;
}

interface FormErrors {
  symbol?: string;
  quantity?: string;
  limitPrice?: string;
  clientOrderId?: string;
  general?: string;
}

const SIDE_OPTIONS = [
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' },
  { value: 'sell_short', label: 'Sell Short' },
  { value: 'sell_short_exempt', label: 'Sell Short Exempt' },
];

const ORDER_TYPE_OPTIONS = [
  { value: 'market', label: 'Market' },
  { value: 'limit', label: 'Limit' },
];

const TIME_IN_FORCE_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'gtc', label: 'Good Till Canceled (GTC)' },
  { value: 'ioc', label: 'Immediate or Cancel (IOC)' },
  { value: 'fok', label: 'Fill or Kill (FOK)' },
];

const initialFormData: FormData = {
  symbol: '',
  side: 'buy',
  orderType: 'limit',
  quantity: '',
  limitPrice: '',
  timeInForce: 'day',
  clientOrderId: '',
};

export function OrderForm({ onSuccess, onCancel }: OrderFormProps) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Symbol validation
    if (!formData.symbol.trim()) {
      newErrors.symbol = 'Symbol is required';
    } else if (!/^[A-Za-z]{1,5}$/.test(formData.symbol.trim())) {
      newErrors.symbol = 'Symbol must be 1-5 letters';
    }

    // Quantity validation
    if (!formData.quantity.trim()) {
      newErrors.quantity = 'Quantity is required';
    } else {
      const qty = parseFloat(formData.quantity);
      if (isNaN(qty) || qty <= 0) {
        newErrors.quantity = 'Quantity must be a positive number';
      }
    }

    // Limit price validation (only for Limit orders)
    if (formData.orderType === 'limit') {
      if (!formData.limitPrice.trim()) {
        newErrors.limitPrice = 'Limit price is required for Limit orders';
      } else {
        const price = parseFloat(formData.limitPrice);
        if (isNaN(price) || price <= 0) {
          newErrors.limitPrice = 'Limit price must be a positive number';
        }
      }
    }

    // Client order ID validation (optional, but validate format if provided)
    if (formData.clientOrderId.trim() && !/^[A-Za-z0-9_-]{1,36}$/.test(formData.clientOrderId.trim())) {
      newErrors.clientOrderId = 'Client Order ID must be alphanumeric with _ or - (max 36 chars)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const request: OrderSubmitRequest = {
        symbol: formData.symbol.trim().toUpperCase(),
        side: formData.side,
        order_type: formData.orderType,
        quantity: formData.quantity.trim(),
        time_in_force: formData.timeInForce,
      };

      // Only include limit_price for Limit orders
      if (formData.orderType === 'limit') {
        request.limit_price = formData.limitPrice.trim();
      }

      // Only include client_order_id if provided
      if (formData.clientOrderId.trim()) {
        request.client_order_id = formData.clientOrderId.trim();
      }

      const response = await ordersApi.submitOrder(request);

      showToast(`Order submitted successfully (${response.client_order_id})`, 'success');
      setFormData(initialFormData);
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit order';
      setErrors({ general: message });
      showToast(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, showToast, onSuccess]);

  const handleReset = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    onCancel?.();
  }, [onCancel]);

  return (
    <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">New Order</h2>

      {errors.general && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {errors.general}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Symbol */}
        <Input
          label="Symbol"
          name="symbol"
          value={formData.symbol}
          onChange={handleChange}
          placeholder="AAPL"
          error={errors.symbol}
          autoComplete="off"
        />

        {/* Side */}
        <SelectInput
          label="Side"
          name="side"
          value={formData.side}
          onChange={handleChange}
          options={SIDE_OPTIONS}
        />

        {/* Order Type */}
        <SelectInput
          label="Order Type"
          name="orderType"
          value={formData.orderType}
          onChange={handleChange}
          options={ORDER_TYPE_OPTIONS}
        />

        {/* Time in Force */}
        <SelectInput
          label="Time in Force"
          name="timeInForce"
          value={formData.timeInForce}
          onChange={handleChange}
          options={TIME_IN_FORCE_OPTIONS}
        />

        {/* Quantity */}
        <Input
          label="Quantity"
          name="quantity"
          type="text"
          inputMode="decimal"
          value={formData.quantity}
          onChange={handleChange}
          placeholder="100"
          error={errors.quantity}
          autoComplete="off"
        />

        {/* Limit Price - only shown for Limit orders */}
        {formData.orderType === 'limit' && (
          <Input
            label="Limit Price"
            name="limitPrice"
            type="text"
            inputMode="decimal"
            value={formData.limitPrice}
            onChange={handleChange}
            placeholder="150.00"
            error={errors.limitPrice}
            autoComplete="off"
          />
        )}

        {/* Client Order ID - optional */}
        <div className={formData.orderType === 'market' ? 'md:col-span-2' : ''}>
          <Input
            label="Client Order ID (Optional)"
            name="clientOrderId"
            value={formData.clientOrderId}
            onChange={handleChange}
            placeholder="my-order-123"
            error={errors.clientOrderId}
            helperText="Leave blank to auto-generate"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="mt-6 flex justify-end space-x-3">
        <Button
          type="button"
          variant="secondary"
          onClick={handleReset}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
        >
          Submit Order
        </Button>
      </div>
    </form>
  );
}
